import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import type { VisualThemeId } from '../theme/visualThemeTypes';
import { VISUAL_THEME_IDS } from '../theme/visualThemeTypes';
import { useLocale } from '../i18n/LocaleContext';
import { controlPill, controlTextS } from '../theme/controlMetrics';

export type ChipPalette = {
  chipBg: string;
  chipBorder: string;
  chipText: string;
  chipSelectedBg: string;
  chipSelectedBorder: string;
  chipSelectedText: string;
};

type Props = {
  visualTheme: VisualThemeId;
  onSelect: (t: VisualThemeId) => void;
  palette: ChipPalette;
};

function labelKey(
  id: VisualThemeId,
): 'visualThemeNeutral' | 'visualThemeVarangian' | 'visualThemeDark' | 'visualThemeAuto' {
  if (id === 'neutral') return 'visualThemeNeutral';
  if (id === 'dark') return 'visualThemeDark';
  if (id === 'auto') return 'visualThemeAuto';
  if (id === 'sun') return 'visualThemeNeutral';
  if (id === 'cosmos') return 'visualThemeDark';
  return 'visualThemeVarangian';
}

export function ThemePickerRow({ visualTheme, onSelect, palette }: Props) {
  const { t } = useLocale();
  return (
    <View style={styles.row} accessibilityRole="radiogroup">
      {VISUAL_THEME_IDS.map((code) => {
        const sel = visualTheme === code;
        return (
          <Pressable
            key={code}
            onPress={() => onSelect(code)}
            style={[
              styles.chip,
              {
                backgroundColor: sel ? palette.chipSelectedBg : palette.chipBg,
                borderColor: sel ? palette.chipSelectedBorder : palette.chipBorder,
              },
            ]}
            accessibilityRole="radio"
            accessibilityState={{ selected: sel }}>
            <Text
              style={[styles.chipText, { color: sel ? palette.chipSelectedText : palette.chipText }]}
              numberOfLines={2}>
              {t(labelKey(code))}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  /**
   * Ряд переносится, а не сжимает чипы в одну строку. Жалоба владельца была буквально
   * «варяжский с переносом буквы й»: четыре чипа на `flex: 1` в строке шириной с телефон
   * дают каждому ~72 px, а «Варяжский» — одно слово без пробелов, разорвать его можно только
   * посимвольно. Так и выходило: «Варяжски» / «й» — и то же самое у «Нейтрального».
   *
   * Перенос по два в ряд (`flexBasis: '48%'`) даёт чипу ~150 px, и все четыре подписи в
   * ru/sr/fr перестают рваться посреди слова: «Авто · Солнце / Космос» переносится по пробелу,
   * остальные встают в строку целиком. Раскладка та же, что у вкладок окна настроек.
   */
  row: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'stretch', width: '100%' },
  // Геометрия общая (`controlMetrics`): ряд стоит прямо под вкладками настроек. Своими
  // остаются только раскладка выше и узкие горизонтальные поля — поля пилюли (14) на самом
  // длинном варианте («Авто · Солнце / Космос») всё ещё съедали бы подпись.
  chip: {
    ...controlPill,
    flexGrow: 1,
    flexBasis: '48%',
    minWidth: 0,
    paddingHorizontal: 6,
  },
  chipText: { ...controlTextS, textAlign: 'center' },
});
